import React, { FC, useState } from 'react';
import { dispatcher } from 'dispatcher';
import { prop } from 'ramda';

import { GameInfoBtn } from '@interlayer/js/components/common/Game/sun/components/GameInfoBtn';

import { IGameTileAssetLayout } from '@core/js/hooks/games/types';
import { useTranslations } from '@core/js/hooks/i18n';
import { useFormatter } from '@core/js/hooks/useFormatter';
import { useDeviceSelector, useUserSelector } from '@core/js/store/hooks';

import { getBingoLabel } from '@interlayer/js/custom/components/main/BingoScheduler/helpers/utilities';
import labels from '@interlayer/js/custom/utils/expirationLabels';
import { clsx } from '@interlayer/js/utils/classNames.util';

import { ListData } from '../types';

import BingoSchedulerTimer from './BingoSchedulerTimer';

export interface Props {
    style: Record<string, any>;
    drawTime: string;
    gameCode: string;
    gameTileAssetsLandscape: IGameTileAssetLayout;
    defaultGameImage: string;
    gameName: string;
    numberOfPlayers: number;
    totalPrize: number;
    cardPrice: number;
    currency: string;
    description: string;
    isGameExclusive: boolean;
    isGameHot: boolean;
    isGameNew: boolean;
    isGamePopular: boolean;
    ticketName: string;
    listData: ListData;
    lobbyName: string;
    jackpotAmount?: number;
}

export const BingoSchedulerItem: FC<Props> = ({
    style,
    drawTime,
    gameCode,
    gameTileAssetsLandscape,
    defaultGameImage,
    gameName,
    numberOfPlayers,
    totalPrize,
    cardPrice,
    currency,
    description,
    isGameExclusive,
    isGameHot,
    isGameNew,
    isGamePopular,
    ticketName,
    listData,
    jackpotAmount,
    lobbyName
}) => {
    const { formatCurrency } = useFormatter();
    const [shouldRemoveGame, setShouldRemoveGame] = useState(false);
    const formFactor = useDeviceSelector(prop('formFactor'));
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const translate = useTranslations();

    const isDesktop = formFactor === 'desktop';
    const currencyValue = formatCurrency(totalPrize / 100, { currency });
    const playButton = translate('playtech.mobile.responsivegames.game.playButton');

    const onClickHandler = () => {
        dispatcher.dispatch('game:launch', { code: gameCode, mode: 0 });
    };

    const { renderNew, renderHot, renderPopular, renderExclusive, renderDefault } = labels.getLabelStatus({
        description,
        name: gameName,
        code: gameCode,
        isNew: isGameNew,
        hot: isGameHot,
        popular: isGamePopular,
        exclusive: isGameExclusive
    }, translate);

    const bubblePriceCalculate = cardPrice / 100;
    const getAmount = (amount: number) => formatCurrency(amount / 100, { currency });
    const bubblePrice = bubblePriceCalculate >= 1
        ? formatCurrency({ currency }) + bubblePriceCalculate
        : `${cardPrice}p`;

    const bingoLabelText = getBingoLabel({ description, name: gameName, code: gameCode });

    return (
        <li
            data-game-code={gameCode}
            className={clsx(style.bingo_scheduler__item, { [style.isRemoved]: shouldRemoveGame })}
            onClick={!isDesktop ? onClickHandler : undefined}
            data-qa-tag="bingo-scheduler-item-card"
        >
            {bingoLabelText && (
                <div className={style.bingo_scheduler__label_flag}>
                    <span className={style.bingo_scheduler__label_flag_text}>{bingoLabelText}</span>
                </div>
            )}

            <div className={clsx(style.bingo_scheduler__item_view, { [style.bingo_scheduler_br_replacer]: bingoLabelText })}>
                <div className={style.bingo_scheduler__header}>
                    <span className={style.bingo_scheduler__label}>{
                        renderHot || renderExclusive || renderPopular || renderNew || renderDefault
                    }
                    </span>

                    <span className={style.bingo_scheduler__bubble}>{bubblePrice}</span>

                    <GameInfoBtn
                        code={gameCode}
                        gameFeed={lobbyName}
                        classNames={{ infoBtn: style.bingoInfoBtn, icon: style.bingoInfoBtnIcon }}
                    />
                </div>

                <div className={style.bingo_scheduler__image_container}>
                    <img
                        className={style.bingo_scheduler__image}
                        src={gameTileAssetsLandscape.layout1x1[isLoggedIn ? 'mainImage' : 'guestMainImage'] || defaultGameImage}
                        alt={gameName}
                    />
                </div>

                <div className={style.bingo_scheduler__details}>
                    <div className={style.bingo_scheduler__details_item}>
                        <div className={style.bingo_scheduler__title}>
                            {ticketName}
                        </div>

                        <div className={style.bingo_scheduler__players}>
                            <span className={style.bingo_scheduler__players_count}>{numberOfPlayers}</span>

                            <span className={style.bingo_scheduler__players_svg} />
                        </div>
                    </div>

                    <div className={clsx(style.bingo_scheduler__details_item, style.bingo_scheduler__delimiter)}>
                        <div className={style.bingo_scheduler__jackpot}>
                            <span className={style.bingo_scheduler__jackpot_svg} />

                            <span className={style.bingo_scheduler__jackpot_sum}>{currencyValue}</span>
                        </div>

                        <BingoSchedulerTimer
                            style={style}
                            drawTime={drawTime}
                            setShouldRemoveGame={setShouldRemoveGame}
                            listData={listData}
                        />

                        {jackpotAmount && (
                            <div className={style.bingo_scheduler__jackpot}>
                                <span className={style.bingo_scheduler__full_jackpot_svg} />

                                <span className={style.bingo_scheduler__jackpot_sum}>
                                    {getAmount(jackpotAmount)}
                                </span>
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        className={style.bingo_scheduler__actions_play}
                        data-qa-tag="bingo-scheduler-start-play"
                        onClick={isDesktop ? onClickHandler : undefined}
                    >
                        {playButton}
                    </button>
                </div>
            </div>
        </li>
    );
};

export default BingoSchedulerItem;
